import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, Plus, UserPlus, UserMinus, GraduationCap, BookOpen,
  Crown, Shield, Search, Trash2, ChevronRight, Layers
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClassGroupsPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [promotions, setPromotions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedPromo, setSelectedPromo] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [students, setStudents] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDlg, setCreateDlg] = useState(false);
  const [assignDlg, setAssignDlg] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMax, setNewMax] = useState(100);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const uniId = user?.university_id || 'josmart-universite-001';

  const loadPromotions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/promotions`, { params: { institution_id: uniId } });
      setPromotions(data);
      if (data.length > 0 && !selectedPromo) {
        setSelectedPromo(data[0].id);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [uniId]);

  const loadGroups = useCallback(async () => {
    if (!selectedPromo) return;
    try {
      const { data } = await axios.get(`${API}/class-groups`, { params: { institution_id: uniId, promotion_id: selectedPromo } });
      setGroups(data);
    } catch (e) { console.error(e); }
  }, [uniId, selectedPromo]);

  const loadStudents = useCallback(async (groupId) => {
    try {
      const { data } = await axios.get(`${API}/class-groups/${groupId}/students`);
      setStudents(data);
    } catch (e) { console.error(e); }
  }, []);

  const loadUnassigned = useCallback(async (groupId) => {
    try {
      const { data } = await axios.get(`${API}/class-groups/${groupId}/unassigned-students`);
      setUnassigned(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadPromotions(); }, [loadPromotions]);
  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => {
    if (selectedGroup) {
      loadStudents(selectedGroup.id);
    }
  }, [selectedGroup, loadStudents]);

  const handleCreateGroup = async () => {
    if (!newName.trim() || !selectedPromo) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/class-groups`, {
        institution_id: uniId, promotion_id: selectedPromo, name: newName.trim(), max_students: newMax,
      });
      toast.success(`Classe "${data.name}" creee`);
      setCreateDlg(false);
      setNewName('');
      setNewMax(100);
      loadGroups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Supprimer cette classe ?')) return;
    try {
      await axios.delete(`${API}/class-groups/${groupId}`);
      toast.success('Classe supprimee');
      if (selectedGroup?.id === groupId) setSelectedGroup(null);
      loadGroups();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleAssignStudents = async (studentIds) => {
    if (!selectedGroup) return;
    try {
      const { data } = await axios.post(`${API}/class-groups/${selectedGroup.id}/assign-students`, {
        student_user_ids: studentIds,
      });
      toast.success(data.message);
      loadStudents(selectedGroup.id);
      loadUnassigned(selectedGroup.id);
      loadGroups();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleRemoveStudent = async (userId) => {
    if (!selectedGroup) return;
    try {
      await axios.post(`${API}/class-groups/${selectedGroup.id}/remove-students`, {
        student_user_ids: [userId],
      });
      toast.success('Etudiant retire');
      loadStudents(selectedGroup.id);
      loadGroups();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleSetLeader = async (userId, role) => {
    if (!selectedGroup) return;
    const field = role === 'leader' ? 'class_leader_user_id' : 'deputy_leader_user_id';
    try {
      await axios.put(`${API}/class-groups/${selectedGroup.id}`, { [field]: userId });
      toast.success(role === 'leader' ? 'Chef de classe designe' : 'Adjoint designe');
      const { data } = await axios.get(`${API}/class-groups`, { params: { institution_id: uniId, promotion_id: selectedPromo } });
      setGroups(data);
      const updated = data.find(g => g.id === selectedGroup.id);
      if (updated) setSelectedGroup(updated);
      loadStudents(selectedGroup.id);
    } catch (e) { toast.error('Erreur'); }
  };

  const openAssignDlg = () => {
    if (!selectedGroup) return;
    loadUnassigned(selectedGroup.id);
    setAssignDlg(true);
    setSearchTerm('');
  };

  const selectedPromoData = promotions.find(p => p.id === selectedPromo);
  const filteredUnassigned = unassigned.filter(s =>
    `${s.first_name} ${s.last_name} ${s.matricule}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="class-groups-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2" data-testid="class-groups-title">
            <Layers className="w-5 h-5" /> Espaces de Classe
          </h1>
          <p className="text-sm text-gray-500">Gestion des classes par promotion</p>
        </div>
      </div>

      {/* Promotion Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex-1 w-full sm:w-auto">
              <Label className="text-xs text-gray-500 mb-1 block">Promotion</Label>
              <Select value={selectedPromo} onValueChange={v => { setSelectedPromo(v); setSelectedGroup(null); }}>
                <SelectTrigger data-testid="promotion-select"><SelectValue placeholder="Choisir une promotion" /></SelectTrigger>
                <SelectContent>
                  {promotions.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.program?.name || 'Programme'} - {p.year_label} ({p.academic_year}) [{p.student_count} etud.]
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedPromo && (
              <Button size="sm" onClick={() => setCreateDlg(true)} className="mt-5 sm:mt-0" data-testid="create-class-btn">
                <Plus className="w-4 h-4 mr-1" /> Nouvelle Classe
              </Button>
            )}
          </div>
          {selectedPromoData && (
            <div className="flex gap-4 mt-3 text-sm text-gray-500">
              <span>{selectedPromoData.student_count} etudiants</span>
              <span>{selectedPromoData.class_group_count} classe(s)</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Groups + Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Groups List */}
        <div className="space-y-2">
          {groups.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-400 text-sm">
                Aucune classe pour cette promotion.
              </CardContent>
            </Card>
          ) : (
            groups.map(g => (
              <Card
                key={g.id}
                className={`cursor-pointer transition-all hover:shadow-md ${selectedGroup?.id === g.id ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setSelectedGroup(g)}
                data-testid={`class-group-card-${g.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium text-sm">{g.name}</p>
                        <p className="text-[10px] text-gray-400">{g.student_count}/{g.max_students} etudiants</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {g.leader_name && <Badge variant="outline" className="text-[10px]"><Crown className="w-3 h-3 mr-0.5" />{g.leader_name.split(' ')[0]}</Badge>}
                      <ChevronRight className="w-4 h-4 text-gray-300" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-2">
          {!selectedGroup ? (
            <Card>
              <CardContent className="py-16 text-center text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Selectionnez une classe pour voir les details</p>
              </CardContent>
            </Card>
          ) : (
            <Card data-testid="class-detail-panel">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-5 h-5" /> {selectedGroup.name}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={openAssignDlg} data-testid="assign-students-btn">
                      <UserPlus className="w-4 h-4 mr-1" /> Affecter
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteGroup(selectedGroup.id)} data-testid="delete-class-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex gap-3 text-xs text-gray-500 mt-1">
                  <span>{selectedGroup.promotion?.year_label} ({selectedGroup.promotion?.academic_year})</span>
                  <span>{selectedGroup.program?.name}</span>
                  <span>{selectedGroup.student_count} etudiants</span>
                </div>
                {(selectedGroup.leader_name || selectedGroup.deputy_name) && (
                  <div className="flex gap-3 mt-1">
                    {selectedGroup.leader_name && <Badge className="bg-yellow-100 text-yellow-700 text-[10px]"><Crown className="w-3 h-3 mr-1" />Chef: {selectedGroup.leader_name}</Badge>}
                    {selectedGroup.deputy_name && <Badge className="bg-blue-100 text-blue-700 text-[10px]"><Shield className="w-3 h-3 mr-1" />Adj: {selectedGroup.deputy_name}</Badge>}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="students">
                  <TabsList>
                    <TabsTrigger value="students"><Users className="w-4 h-4 mr-1" />Etudiants ({students.length})</TabsTrigger>
                    <TabsTrigger value="courses"><BookOpen className="w-4 h-4 mr-1" />Infos</TabsTrigger>
                  </TabsList>
                  <TabsContent value="students">
                    <ScrollArea className="h-[350px]">
                      {students.length === 0 ? (
                        <p className="text-center text-gray-400 text-sm py-8">Aucun etudiant. Cliquez "Affecter" pour ajouter.</p>
                      ) : (
                        <div className="space-y-1">
                          {students.map(s => (
                            <div key={s.user_id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50 text-sm" data-testid={`student-row-${s.matricule}`}>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                                  {s.first_name?.[0]}{s.last_name?.[0]}
                                </div>
                                <div>
                                  <p className="font-medium">{s.first_name} {s.last_name}</p>
                                  <p className="text-[10px] text-gray-400">{s.matricule}</p>
                                </div>
                                {s.is_leader && <Badge className="bg-yellow-100 text-yellow-700 text-[10px]"><Crown className="w-3 h-3 mr-0.5" />Chef</Badge>}
                                {s.is_deputy && <Badge className="bg-blue-100 text-blue-700 text-[10px]"><Shield className="w-3 h-3 mr-0.5" />Adjoint</Badge>}
                              </div>
                              <div className="flex gap-1">
                                {!s.is_leader && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Nommer chef" onClick={() => handleSetLeader(s.user_id, 'leader')}>
                                    <Crown className="w-3.5 h-3.5 text-yellow-600" />
                                  </Button>
                                )}
                                {!s.is_deputy && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Nommer adjoint" onClick={() => handleSetLeader(s.user_id, 'deputy')}>
                                    <Shield className="w-3.5 h-3.5 text-blue-600" />
                                  </Button>
                                )}
                                <Button size="icon" variant="ghost" className="h-7 w-7" title="Retirer" onClick={() => handleRemoveStudent(s.user_id)}>
                                  <UserMinus className="w-3.5 h-3.5 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="courses">
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <p className="text-xl font-bold text-blue-600">{selectedGroup.student_count}</p>
                          <p className="text-[10px] text-gray-500">Etudiants</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <p className="text-xl font-bold text-green-600">{selectedGroup.max_students}</p>
                          <p className="text-[10px] text-gray-500">Capacite max</p>
                        </div>
                      </div>
                      <Separator />
                      <p className="text-gray-500 text-xs">Promotion: {selectedGroup.promotion?.year_label} - {selectedGroup.promotion?.academic_year}</p>
                      <p className="text-gray-500 text-xs">Programme: {selectedGroup.program?.name || '-'}</p>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDlg} onOpenChange={setCreateDlg}>
        <DialogContent data-testid="create-class-dialog">
          <DialogHeader><DialogTitle>Nouvelle Classe</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom de la classe *</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Groupe A, Section 1..." data-testid="class-name-input" />
            </div>
            <div>
              <Label>Capacite maximale</Label>
              <Input type="number" value={newMax} onChange={e => setNewMax(parseInt(e.target.value) || 100)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDlg(false)}>Annuler</Button>
            <Button onClick={handleCreateGroup} disabled={!newName.trim() || creating} data-testid="confirm-create-class">
              {creating ? 'Creation...' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Students Dialog */}
      <Dialog open={assignDlg} onOpenChange={setAssignDlg}>
        <DialogContent className="max-w-lg" data-testid="assign-students-dialog">
          <DialogHeader><DialogTitle>Affecter des etudiants a {selectedGroup?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <Input className="pl-9" placeholder="Rechercher par nom ou matricule..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-unassigned" />
            </div>
            <p className="text-xs text-gray-400">{filteredUnassigned.length} etudiant(s) non affecte(s)</p>
            <ScrollArea className="h-[300px]">
              {filteredUnassigned.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-8">Tous les etudiants sont deja affectes</p>
              ) : (
                <div className="space-y-1">
                  <Button size="sm" variant="outline" className="w-full mb-2" onClick={() => handleAssignStudents(filteredUnassigned.map(s => s.user_id))} data-testid="assign-all-btn">
                    <UserPlus className="w-4 h-4 mr-1" /> Affecter tous ({filteredUnassigned.length})
                  </Button>
                  {filteredUnassigned.map(s => (
                    <div key={s.user_id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50 text-sm">
                      <div>
                        <p className="font-medium">{s.first_name} {s.last_name}</p>
                        <p className="text-[10px] text-gray-400">{s.matricule}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleAssignStudents([s.user_id])}>
                        <UserPlus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
